import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './util.js';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** The hero card: secondary container, large corners, room for a display number. */
  hero?: boolean;
  /** Drawn on the page colour with an outline, for a danger zone. */
  variant?: 'outlined';
  children: ReactNode;
}

export function Card({ hero = false, variant, className, children, ...rest }: CardProps) {
  return (
    <div
      {...rest}
      className={cx(
        'k-card',
        hero && 'k-card--hero',
        variant === 'outlined' && 'k-card--outlined',
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface StatTileProps {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  /** Makes the tile a button that opens the detail. */
  onClick?: () => void;
  tone?: 'default' | 'error' | 'highlight';
  /** A smaller figure, for three tiles side by side. */
  compact?: boolean;
}

/** A figure with its name under it. */
export function StatTile({
  label,
  value,
  note,
  onClick,
  tone = 'default',
  compact = false,
}: StatTileProps) {
  const body = (
    <>
      <span className={compact ? 'k-t-title-sm' : 'k-t-money'}>{value}</span>
      <span className="k-t-label-sm k-muted">{label}</span>
      {note === undefined ? null : <span className="k-t-label-sm k-muted">{note}</span>}
    </>
  );
  const cls = cx(
    'k-stat',
    tone === 'error' && 'k-stat--error',
    tone === 'highlight' && 'k-stat--highlight',
    compact && 'k-stat--compact',
  );
  return onClick === undefined ? (
    <div className={cls}>{body}</div>
  ) : (
    <button type="button" className={cx(cls, 'k-layer')} onClick={onClick}>
      {body}
    </button>
  );
}

export interface ListProps {
  children: ReactNode;
  label?: string;
}

/** A segmented list: items are 2 dp apart, the outer corners are large and the inner small. */
export function List({ children, label }: ListProps) {
  return (
    <ul className="k-list" aria-label={label}>
      {children}
    </ul>
  );
}

export interface ListItemProps {
  title: ReactNode;
  supporting?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  href?: string;
  selected?: boolean;
  disabled?: boolean;
  /** A button beside a clickable row (a row cannot hold one itself). */
  action?: ReactNode;
  /** Quiet status tags under the supporting line. */
  tags?: ReactNode;
}

export function ListItem({
  title,
  supporting,
  leading,
  trailing,
  onClick,
  href,
  selected,
  disabled,
  action,
  tags,
}: ListItemProps) {
  const content = (
    <>
      {leading === undefined ? null : <span className="k-list__lead">{leading}</span>}
      <span className="k-list__text">
        <span className="k-t-title-sm">{title}</span>
        {supporting === undefined ? null : (
          <span className="k-list__sub k-muted">{supporting}</span>
        )}
        {tags === undefined ? null : <span className="k-list__tags">{tags}</span>}
      </span>
      {trailing === undefined ? null : <span className="k-list__trail">{trailing}</span>}
    </>
  );
  const cls = cx('k-list__row k-layer', selected && 'k-list__row--selected');
  return (
    <li className={cx('k-list__item', action !== undefined && 'k-list__item--action')}>
      {href !== undefined ? (
        <a href={href} className={cls} aria-current={selected ? 'true' : undefined}>
          {content}
        </a>
      ) : onClick !== undefined ? (
        <button
          type="button"
          className={cls}
          onClick={onClick}
          disabled={disabled}
          aria-pressed={selected}
        >
          {content}
        </button>
      ) : (
        <div className={cls}>{content}</div>
      )}
      {action === undefined ? null : <span className="k-list__action">{action}</span>}
    </li>
  );
}

export function Skeleton({
  width,
  height = 16,
  className,
}: {
  width?: number | string;
  height?: number | string;
  className?: string;
}) {
  return (
    <span aria-hidden="true" className={cx('k-skeleton', className)} style={{ width, height }} />
  );
}

export interface BadgeProps {
  count?: number;
  /** Spoken text, for example "3 new". */
  label: string;
}

export function Badge({ count, label }: BadgeProps) {
  return (
    <span
      className={cx('k-badge', count === undefined && 'k-badge--dot')}
      role="status"
      aria-label={label}
    >
      {count === undefined ? null : count > 99 ? '99+' : count}
    </span>
  );
}

export interface AvatarProps {
  /** Up to two letters. */
  initials: string;
  size?: 'sm' | 'md' | 'lg';
  /** One tone for every avatar; a signed or waiting state is shown by the caller. */
  muted?: boolean;
  /** True (the default) when the name is said elsewhere; false when the initials are the button's visible text. */
  decorative?: boolean;
  className?: string;
}

/** A circle with initials: people in a list, the signed in person in the top bar. */
export function Avatar({
  initials,
  size = 'md',
  muted = false,
  decorative = true,
  className,
}: AvatarProps) {
  return (
    <span
      className={cx('k-avatar', `k-avatar--${size}`, muted && 'k-avatar--muted', className)}
      {...(decorative ? { 'aria-hidden': true } : {})}
    >
      {initials}
    </span>
  );
}
